import { AlertCircle, MoreVerticalIcon, UserMinusIcon } from "lucide-react";
import { useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { PublicEvent } from "@classview/shared-types";
import { useQueryClient } from "@tanstack/react-query";

import { useUpdateEvent } from "../api/update-event";

type EventActionsProps = {
  event: PublicEvent;
  attendeeEmail: string;
};

export function AttendeesActions({ event, attendeeEmail }: EventActionsProps) {
  const { organisation } = useOrganisation();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const {
    mutateAsync: updateEvent,
    isPending,
    isError,
    error,
  } = useUpdateEvent({
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey.includes("event"),
      });
      setIsDialogOpen(false);
    },
  });

  const handleRemoveAttendee = async () => {
    await updateEvent({
      eventId: event.id,
      orgId: organisation.id,
      event: {
        session: {
          guests: event.session.guests.filter(
            (guest) => guest !== attendeeEmail,
          ),
        },
      },
    });
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DropdownMenu modal={false}>
        <div className="flex w-full justify-end">
          <div className="w-9">
            <DropdownMenuTrigger asChild className="flex w-full justify-end">
              <Button variant="ghost" size="icon" className="justify-center">
                <MoreVerticalIcon className="size-4" aria-hidden={true} />
              </Button>
            </DropdownMenuTrigger>
          </div>
        </div>
        <DropdownMenuContent align="end">
          <DialogTrigger asChild>
            <DropdownMenuItem className="cursor-pointer text-destructive">
              <UserMinusIcon className="size-4" aria-hidden={true} />
              Remove Attendee
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Are you sure you would like to remove this attendee?
          </DialogTitle>
          <DialogDescription>This action cannot be undone.</DialogDescription>
        </DialogHeader>
        {isError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unexpected error</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            type="submit"
            onClick={handleRemoveAttendee}
            disabled={isPending}
            isLoading={isPending}
            isAnimated={isPending}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
